import graphql__d85164e750b5e1dcfc6a from "./__generated__/UserSync_viewer.graphql.ts";
import graphql__adb3614b5a2a199318cf from "./__generated__/UserSyncQuery.graphql.ts";
import graphql__66c4d7a609ff0f95607f from "./__generated__/UserSyncMutation.graphql.ts";
import graphql__297e98545a90363a8655 from "./__generated__/UserSyncLoginUserMutation.graphql.ts";
import graphql__a8008087374485678e82 from "./__generated__/UserSyncEmailMutation.graphql.ts";/* eslint-disable relay/unused-fields */
import { User } from '@auth0/auth0-react'
import { Alert } from '@portal/components'
import * as Sentry from '@sentry/browser'
import graphql from 'babel-plugin-relay/macro'
import { useCreateHubSpotContact, useTrackContactVisit } from 'components/hooks'
import { MainLayout } from 'components/layout'
import { FullPageLoader } from 'components/loader'
import React, { useEffect, useState } from 'react'
import { useLazyLoadQuery, useRefetchableFragment } from 'react-relay/hooks'
import { usePortalMutation } from 'utils'
import { useAnalytics } from 'utils/analytics'

import { UserSync_viewer$key } from './__generated__/UserSync_viewer.graphql'
import { UserSyncEmailMutation } from './__generated__/UserSyncEmailMutation.graphql'
import { UserSyncLoginUserMutation } from './__generated__/UserSyncLoginUserMutation.graphql'
import { UserSyncMutation } from './__generated__/UserSyncMutation.graphql'
import { UserSyncQuery } from './__generated__/UserSyncQuery.graphql'
import { UserSyncQueryRefetch } from './__generated__/UserSyncQueryRefetch.graphql'
import { useInitLD } from './useInitLD'

const CurrentUserFragment = graphql__d85164e750b5e1dcfc6a

const query = graphql__adb3614b5a2a199318cf

export const CreateUnknownUserMutation = graphql__66c4d7a609ff0f95607f

export const LoginUserMutation = graphql__297e98545a90363a8655

export const SyncEmailMutation = graphql__a8008087374485678e82

type Props = {
  /** User data extracted from auth0. */
  user: User
  /** Children. */
  children: (
    userInfo: {
      readonly id: string
      readonly email: string
      readonly isSuperAdmin: boolean
    } | null,
  ) => React.ReactNode
}

/** Component used to sync the current user data to the portal
 * when a user with a valid JWT token doesn't exist on the server.
 *
 * This ensures that all users authenticated through the Auth provider
 * have been saved on our server.
 */
export default function UserSync({ user, children }: Props) {
  const { setUserId, logEvent } = useAnalytics()
  const [didRefetchViewer, setDidRefetchViewer] = useState(false)
  const { viewer } = useLazyLoadQuery<UserSyncQuery>(query, {})
  const [data, refetch] = useRefetchableFragment<
    UserSyncQueryRefetch,
    UserSync_viewer$key
  >(CurrentUserFragment, viewer)
  const [createUser] = usePortalMutation<UserSyncMutation>(
    CreateUnknownUserMutation,
    {
      mutationName: 'CreateUnknownUser',
      sentryContext: {
        component: 'UserSync',
      },
    },
  )
  const [syncUserEmail] = usePortalMutation<UserSyncEmailMutation>(
    SyncEmailMutation,
    {
      mutationName: 'SyncUserEmail',
      sentryContext: {
        component: 'UserSync',
      },
    },
  )
  const [loginUser] = usePortalMutation<UserSyncLoginUserMutation>(
    LoginUserMutation,
    {
      mutationName: 'LoginUser',
      sentryContext: {
        component: 'UserSync',
      },
    },
  )
  const viewerId = data?.me?.id
  const currentEmail = data?.me?.email
  const currentName = user.name
  const hubspotContactId = useCreateHubSpotContact(viewerId)
  useTrackContactVisit(hubspotContactId)
  useInitLD({
    key: viewerId,
    email: currentEmail,
    name: currentName,
    isRunningCypress: window.Cypress !== undefined,
  })

  useEffect(() => {
    if (viewerId && currentEmail) {
      setUserId(viewerId, currentEmail)

      Sentry.setUser({ id: viewerId, email: currentEmail })
    }
  }, [viewerId, currentEmail, setUserId])

  useEffect(() => {
    const props = (logEventType: string) => ({
      onCompleted() {
        logEvent(logEventType)
        refetch(
          {},
          {
            fetchPolicy: 'network-only',
            onComplete: () => {
              setDidRefetchViewer(true)
            },
          },
        )
      },
    })

    if (!viewerId && user) {
      createUser({
        variables: {
          input: {
            auth0Id: user.sub!,
            firstName: user.given_name,
            lastName: user.family_name,
            email: user.email!,
            imageUrl: user.picture,
          },
        },
        ...props('createdAccount'),
      })
    } else if (viewerId) {
      logEvent('userLogin')
    }
  }, [viewerId, createUser, user, refetch, logEvent])

  useEffect(() => {
    if (viewerId) {
      loginUser({ variables: {} })
    }
  }, [viewerId, loginUser])

  /** An effect to update portal emails if they don't match the one in Auth0. */
  useEffect(() => {
    if (viewerId && user.email && currentEmail !== user.email) {
      // Only sync emails for Ditto accounts using Google SSO, report to Sentry otherwise.
      if (
        !user.sub?.startsWith('google-oauth2|') ||
        !/@ditto\.(live|com)$/i.test(currentEmail ?? '')
      ) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            currentEmail,
            newEmail: user.email,
            auth0Id: user.sub,
          })
          Sentry.captureMessage(
            "User's email doesn't match the one in Auth0 but won't be synced",
            'error',
          )
        })
        return
      }

      syncUserEmail({
        variables: {},
        onError: (err) => {
          Sentry.withScope((scope) => {
            scope.setTag('UserSync', 'error trying to sync email')
            scope.setExtras({
              currentEmail,
              newEmail: user.email,
              auth0Id: user.sub,
            })
            Sentry.captureException(err)
          })
        },
      })
    }
  }, [viewerId, currentEmail, user.sub, user.email, syncUserEmail])

  if (!viewerId) {
    return didRefetchViewer ? (
      <MainLayout>
        <Alert variant="danger">
          There was an error starting the portal session.
        </Alert>
      </MainLayout>
    ) : (
      <FullPageLoader />
    )
  }

  // Can't return children directly
  // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051#issuecomment-449628575
  return <>{children(data?.me)}</>
}
